import { useState, useCallback } from "react";
import { useNavigate, useLoaderData } from "react-router-dom";
import { createPartner, updatePartner, getUploadUrl } from "../../../api/cms";
import Toast from "../../../components/Toast";
import { resizeImage } from "../../../utils/resize-image";

const inputClass = "border border-gray-300 rounded px-3 py-2 font-normal focus:outline-none focus:ring-2 focus:ring-bahyellow";

export default function AdminPartnerForm() {
  const existing = useLoaderData();
  const navigate = useNavigate();
  const [form, setForm] = useState({ name: "", link: "", logo: "", ...existing });
  const [saving, setSaving] = useState(false);
  const [uploading, setUploading] = useState(false);
  const [toast, setToast] = useState(null);
  const clearToast = useCallback(() => setToast(null), []);

  const set = (field) => (e) => setForm((f) => ({ ...f, [field]: e.target.value }));

  const handleLogoUpload = async (file) => {
    setUploading(true);
    setToast(null);
    try {
      const { blob, contentType } = await resizeImage(file, { maxWidth: 1080, maxHeight: 1080 });
      const ext = contentType === 'image/png' ? 'png' : 'jpg';
      const key = `img/partners/${crypto.randomUUID()}.${ext}`;
      const { url } = await getUploadUrl(key, contentType);
      const res = await fetch(url, { method: 'PUT', body: blob, headers: { 'Content-Type': contentType } });
      if (!res.ok) throw new Error(`S3 error ${res.status}`);
      setForm((f) => ({ ...f, logo: `https://brusselsafricahub.eu/${key}` }));
      setToast({ message: 'Logo uploaded', type: 'success' });
    } catch (err) {
      setToast({ message: `Upload failed: ${err.message}`, type: 'error' });
    } finally {
      setUploading(false);
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setSaving(true);
    setToast(null);
    try {
      if (existing?.id) {
        await updatePartner(existing.id, form);
      } else {
        await createPartner(form);
      }
      navigate("/admin/partners");
    } catch {
      setToast({ message: 'Failed to save. Check your API key and try again.', type: 'error' });
    } finally {
      setSaving(false);
    }
  };

  return (
    <div className="max-w-lg">
      <Toast message={toast?.message} type={toast?.type} onClose={clearToast} />
      <h1 className="text-2xl font-bold mb-6">{existing?.id ? "Edit partner" : "Add partner"}</h1>
      <form onSubmit={handleSubmit} className="bg-white rounded-lg shadow p-6 flex flex-col gap-4">
        <label className="flex flex-col gap-1 text-sm font-medium">
          Name
          <input className={inputClass} value={form.name} onChange={set("name")} required />
        </label>
        <label className="flex flex-col gap-1 text-sm font-medium">
          Website URL
          <input className={inputClass} value={form.link} onChange={set("link")} type="url" required />
        </label>

        <div className="flex flex-col gap-2 text-sm font-medium">
          Logo
          <div className="flex items-center gap-4">
            {form.logo
              ? <img src={form.logo} alt="Logo" className="h-12 object-contain" />
              : <span className="text-gray-400 text-xs">No logo yet</span>
            }
            <label className={`cursor-pointer text-xs text-white rounded px-3 py-1.5 font-semibold ${uploading ? 'bg-gray-300' : 'bg-gray-500 hover:bg-gray-600'}`}>
              {uploading ? 'Uploading…' : form.logo ? 'Replace' : 'Upload'}
              <input type="file" accept="image/*" className="hidden" disabled={uploading} onChange={(e) => { const f = e.target.files[0]; if (f) handleLogoUpload(f); e.target.value = ''; }} />
            </label>
          </div>
        </div>

        <div className="flex gap-3 mt-2">
          <button type="submit" disabled={saving} className="bg-bahyellow rounded px-4 py-2 text-sm font-semibold hover:opacity-90 disabled:opacity-50">
            {saving ? "Saving…" : "Save"}
          </button>
          <button type="button" onClick={() => navigate("/admin/partners")} className="rounded px-4 py-2 text-sm border border-gray-300 hover:bg-gray-50">
            Cancel
          </button>
        </div>
      </form>
    </div>
  );
}
